/** @file EventRow: the playhead clip's key events as chips at their frames; click one to edit it. */
import type { ClipEvent } from '@zilo/pose3d-format/types/clip';
import type { ReactElement } from 'react';
import { useState } from 'react';
import { useEditor } from '../../state/useEditor';
import { useEditorStore } from '../../state/useEditorStore';
import { EventEditor } from './EventEditor';
import { FRAME_WIDTH } from './frameWidth';
import { usePlayheadClip } from './usePlayheadClip';

/** Which chip is open: the key frame and the event's index in it. */
type Editing = { frame: number; index: number } | null;

const chip = { position: 'absolute' as const, top: 1, fontSize: 9, padding: '0 3px', borderRadius: 3, whiteSpace: 'nowrap' as const, cursor: 'pointer',
  background: 'var(--event-bg, #2d2440)', border: '1px solid var(--border, #2a2e38)' };

/** Chips `type·node` stacked per frame; saving or deleting rewrites that frame's events (`clip.setEvents`). */
export function EventRow(): ReactElement | null {
  const store = useEditorStore();
  const cur = usePlayheadClip();
  const nodes = useEditor((s) => s.puppet.doc?.puppet.nodes);
  const [editing, setEditing] = useState<Editing>(null);
  if (cur === null) return null;
  const keyed = cur.clip.keys.filter((k) => (k.events ?? []).length > 0);
  const open = editing === null ? undefined : cur.clip.keys.find((k) => k.frame === editing.frame)?.events?.[editing.index];
  const write = (events: ClipEvent[]): void => {
    if (editing === null) return;
    void store.getState().dispatch([{ name: 'clip.setEvents', input: { clip: cur.name, frame: editing.frame, events } }], `events @${editing.frame}`);
    setEditing(null);
  };
  const others = (): ClipEvent[] => (cur.clip.keys.find((k) => k.frame === editing?.frame)?.events ?? []).filter((_, i) => i !== editing?.index);
  return (
    <div data-row="events">
      <div style={{ display: 'flex', fontSize: 11 }}>
        <span style={{ width: 90, flex: 'none', paddingLeft: 4, color: 'var(--muted, #8b919e)' }}>events</span>
        <div style={{ position: 'relative', height: 16 + 12 * Math.max(0, ...keyed.map((k) => (k.events ?? []).length - 1)), width: (cur.clip.frames + 1) * FRAME_WIDTH }}>
          {keyed.flatMap((k) => (k.events ?? []).map((ev, i) => (
            <span key={`${k.frame}-${i}`} role="button" aria-label={`event ${k.frame}.${i} ${ev.type}`}
              style={{ ...chip, left: k.frame * FRAME_WIDTH, top: 1 + 12 * i }} onClick={() => { setEditing({ frame: k.frame, index: i }); }}>
              {ev.type}{ev.node === undefined ? '' : `·${ev.node}`}
            </span>
          )))}
        </div>
      </div>
      {editing !== null && open !== undefined && (
        <EventEditor key={`${editing.frame}-${editing.index}`} event={open} nodes={Object.keys(nodes ?? {})}
          onSave={(ev) => { const list = others(); list.splice(editing.index, 0, ev); write(list); }}
          onDelete={() => { write(others()); }} onCancel={() => { setEditing(null); }} />
      )}
    </div>
  );
}
